import dayjs from 'dayjs';
import z from 'zod';

type EnumProps = {
  label: string;
  value: string;
};

export const status = [
  { label: 'Draft', value: 'draft' },
  { label: 'ROM', value: 'rom' },
  { label: 'Submitted', value: 'submitted' },
  { label: 'Approved', value: 'approved' },
  { label: 'Rejected', value: 'rejected' },
  { label: 'Converted', value: 'converted' },
] as const satisfies ReadonlyArray<EnumProps>;

const moneyString = z
  .string()
  .optional()
  .transform((v) => {
    const s = (v ?? '').trim();
    if (!s) return undefined;
    return s.replace(/[$, ]/g, '');
  })
  .refine((v) => v === undefined || /^-?\d+(\.\d+)?$/.test(v), {
    message: 'A valid number is required.',
  });

// ----------------------
// POST /api/rfi/pending-changes/
// ----------------------
export const post = {
  body: z.object({
    project: z.coerce.number({ error: 'Project is required' }),

    // base draft creation requires title
    title: z.string().nonempty({ error: 'Title is required' }),

    description: z.string().optional(),

    status: z.enum(status.map((item) => item.value)).default('draft'),

    // UI uses <input type="date"> => YYYY-MM-DD
    due_at: z
      .string()
      .optional()
      .refine((val) => val === '' || dayjs(val, 'YYYY-MM-DD', true).isValid(), {
        message: 'Select a valid date.',
      }),

    ball_in_court: z.string().trim().optional().or(z.literal('')),

    submitted_amount: moneyString,
    approved_amount: moneyString,

    co_number: z.string().trim().optional().or(z.literal('')),

    schedule_impact_days: z
      .union([z.coerce.number().int().optional(), z.undefined()])
      .optional(),

    refs: z.any().optional(),
  }),
  response: z.object({
    code: z.string(),
    message: z.string(),
    detail: z.string().optional(),
    data: z.object({
      id: z.number(),
      project: z.number(),
      cp_number: z.string(),
      title: z.string(),
      description: z.string().nullable().optional(),
      status: z.enum(status.map((item) => item.value)),
      due_at: z.date().nullable().optional(),
      ball_in_court: z.string().nullable().optional(),
      submitted_at: z.date().nullable().optional(),
      approved_at: z.date().nullable().optional(),
      rejected_at: z.date().nullable().optional(),
      co_number: z.string().nullable().optional(),
      submitted_amount: z.any().nullable().optional(),
      pending_amount: z.any(),
      approved_amount: z.any().nullable().optional(),
      approved_accounting_amount: z.any(),
      schedule_impact_days: z.number().nullable().optional(),
      refs: z.any().nullable().optional(),
      created_by: z.number().nullable().optional(),
      created_by_name: z.string().nullable().optional(),
      attachments: z.array(z.object({ id: z.number() })).optional(),
      created_at: z.date(),
      updated_at: z.date(),
    }),
    errors: z.any().optional(),
  }),
  params: z.never(),
  query: z.never(),
};
const postSchema = z.object(post);
export type Post = z.infer<typeof postSchema>;

// ----------------------
// PATCH /api/rfi/pending-changes/:id
// ----------------------
export const patch = {
  body: post.body.partial(),
  response: z.object({
    code: z.string(),
    message: z.string(),
    detail: z.string().optional(),
    data: z.any().optional(),
    errors: z.any().optional(),
  }),
  params: z.object({
    pending_change_id: z.coerce.number(),
  }),
  query: z.never(),
};
const patchSchema = z.object(patch);
export type Patch = z.infer<typeof patchSchema>;

// ----------------------
// GET /api/rfi/pending-changes/?project=...
// ----------------------
const get = {
  body: z.never(),
  response: z.object({
    code: z.string(),
    message: z.string(),
    detail: z.string().optional(),
    data: z.array(
      z.object({
        id: z.number(),
        cp_number: z.string(),
        title: z.string(),
        project_id: z.number(),
        status: z.enum(status.map((item) => item.value)),
        due_at: z.date().nullable().optional(),
        ball_in_court: z.string().nullable().optional(),
        submitted_at: z.date().nullable().optional(),
        approved_at: z.date().nullable().optional(),
        rejected_at: z.date().nullable().optional(),
        co_number: z.string().nullable().optional(),
        submitted_amount: z.any().nullable().optional(),
        pending_amount: z.any(),
        approved_amount: z.any().nullable().optional(),
        approved_accounting_amount: z.any(),
        is_overdue: z.boolean(),
      }),
    ),
    summary: z.object({
      submitted_total: z.any(),
      pending_total: z.any(),
      approved_total: z.any(),
      original_contract_value: z.any(),
      revised_contract_value: z.any(),
    }),
    errors: z.any().optional(),
  }),
  params: z.never(),
  query: z.object({
    project: z.coerce.number({ error: 'Project is required' }),
    status: z.string().optional(),
    q: z.string().optional(),
    overdue: z.boolean().optional(),
    due_this_week: z.boolean().optional(),
    section: z.enum(['pending', 'included', 'all']).optional(),
  }),
};
const getSchema = z.object(get);
export type Get = z.infer<typeof getSchema>;

// ----------------------
// GET /api/rfi/pending-changes/:id
// ----------------------
export const getPendingChange = {
  body: z.never(),
  response: z.object({
    code: z.string(),
    message: z.string(),
    detail: z.string().optional(),
    data: z.object({
      id: z.number(),
      project: z.number(),
      cp_number: z.string(),
      title: z.string(),
      description: z.string().nullable().optional(),
      status: z.enum(status.map((item) => item.value)),
      due_at: z.date().nullable().optional(),
      ball_in_court: z.string().nullable().optional(),
      submitted_at: z.date().nullable().optional(),
      approved_at: z.date().nullable().optional(),
      rejected_at: z.date().nullable().optional(),
      co_number: z.string().nullable().optional(),
      submitted_amount: z.any().nullable().optional(),
      pending_amount: z.any(),
      approved_amount: z.any().nullable().optional(),
      approved_accounting_amount: z.any(),
      schedule_impact_days: z.number().nullable().optional(),
      refs: z.any().nullable().optional(),
      created_by: z.number().nullable().optional(),
      created_by_name: z.string().nullable().optional(),
      attachments: z.array(
        z.object({
          id: z.number(),
          pending_change: z.number(),
          filename: z.string(),
          file: z.string().nullable().optional(),
          external_file_url: z.string().nullable().optional(),
          uploaded_by: z.number().nullable().optional(),
          created_at: z.date(),
          presigned_url: z.string(),
        }),
      ),
      created_at: z.date(),
      updated_at: z.date(),
    }),
    errors: z.any().optional(),
  }),
  params: z.object({
    pending_change_id: z.coerce.number(),
  }),
  query: z.never(),
};
const getPendingChangeSchema = z.object(getPendingChange);
export type GetPendingChange = z.infer<typeof getPendingChangeSchema>;

// ----------------------
// Activity list (if you added /activity/ endpoint)
// GET /api/rfi/pending-changes/:id/activity/
// ----------------------
export const getPendingChangeActivity = {
  body: z.never(),
  response: z.object({
    code: z.string(),
    message: z.string(),
    data: z.array(
      z.object({
        id: z.number(),
        activity_type: z.string(),
        user: z.number().nullable().optional(),
        user_name: z.string().nullable().optional(),
        description: z.string(),
        metadata: z.any().nullable().optional(),
        created_at: z.date(),
      }),
    ),
  }),
  params: z.object({
    pending_change_id: z.coerce.number(),
  }),
  query: z.never(),
};
const getPendingChangeActivitySchema = z.object(getPendingChangeActivity);
export type GetPendingChangeActivity = z.infer<
  typeof getPendingChangeActivitySchema
>;

// ----------------------
// POST /api/rfi/pending-changes/:id/attachments
// ----------------------
export const postPendingChangeAttachments = {
  body: z.array(
    z.object({
      filename: z.string().nonempty({ error: 'Filename is required' }),
      file: z.url().nonempty({ error: 'File url is required' }),
    }),
  ),
  response: z.array(
    z.object({
      filename: z.string(),
      file: z.url(),
    }),
  ),
  params: z.object({
    pending_change_id: z.coerce.number(),
  }),
  query: z.never(),
};
const postPendingChangeAttachmentsSchema = z.object(
  postPendingChangeAttachments,
);
export type PostPendingChangeAttachments = z.infer<
  typeof postPendingChangeAttachmentsSchema
>;

// ----------------------
// DELETE attachment
// ----------------------
export const deletePendingChangeAttachment = {
  body: z.never(),
  response: z.object({
    code: z.string(),
    message: z.string(),
    detail: z.string().optional(),
    data: z.any().optional(),
    errors: z.any().optional(),
  }),
  params: z.object({
    pending_change_id: z.coerce.number(),
    attachment_id: z.coerce.number(),
  }),
  query: z.never(),
};
const deletePendingChangeAttachmentSchema = z.object(
  deletePendingChangeAttachment,
);
export type DeletePendingChangeAttachment = z.infer<
  typeof deletePendingChangeAttachmentSchema
>;

// ----------------------
// GET /api/rfi/pending-changes/sheet-settings/?project=...
// ----------------------
export const getPendingChangeSheetSettings = {
  body: z.never(),
  response: z.object({
    code: z.string(),
    message: z.string(),
    detail: z.string().optional(),
    data: z.object({
      project_id: z.number(),
      sheet_title: z.string().nullable().optional(),
      job_no: z.string().nullable().optional(),
      job_name: z.string().nullable().optional(),
      contractor: z.string().nullable().optional(),
      contract_value: z.any().nullable().optional(),

      updated_at: z.date().nullable().optional(),
    }),
    errors: z.any().optional(),
  }),
  params: z.never(),
  query: z.object({
    project: z.coerce.number({ error: 'Project is required' }),
  }),
};
const getPendingChangeSheetSettingsSchema = z.object(
  getPendingChangeSheetSettings,
);
export type GetPendingChangeSheetSettings = z.infer<
  typeof getPendingChangeSheetSettingsSchema
>;

// ----------------------
// PATCH /api/rfi/pending-changes/sheet-settings/?project=...
// ----------------------
export const patchPendingChangeSheetSettings = {
  body: z.object({
    sheet_title: z.string().optional().nullable(),
    job_no: z.string().optional().nullable(),
    job_name: z.string().optional().nullable(),
    contractor: z.string().optional().nullable(),
    contract_value: z.any().nullable().optional(),
  }),
  response: z.object({
    code: z.string(),
    message: z.string(),
    detail: z.string().optional(),
    data: z.any().optional(),
    errors: z.any().optional(),
  }),
  params: z.never(),
  query: z.object({
    project: z.coerce.number({ error: 'Project is required' }),
  }),
};
const patchPendingChangeSheetSettingsSchema = z.object(
  patchPendingChangeSheetSettings,
);
export type PatchPendingChangeSheetSettings = z.infer<
  typeof patchPendingChangeSheetSettingsSchema
>;

// ----------------------
// Presign upload
// POST /api/rfi/pending-changes/attachments/presign/
// ----------------------
export const presignUpload = {
  body: z.object({
    files: z.array(
      z.object({
        filename: z.string().nonempty({ error: 'Filename is required' }),
        content_type: z
          .string()
          .nonempty({ error: 'Content type is required' }),
        size: z.number().int().nonnegative().optional(),
      }),
    ),
  }),
  response: z.object({
    presigned_urls: z.array(
      z.object({
        filename: z.string(),
        upload_url: z.string(),
        file_url: z.string(),
      }),
    ),
  }),
  params: z.never(),
  query: z.never(),
};
const presignUploadSchema = z.object(presignUpload);
export type PresignUpload = z.infer<typeof presignUploadSchema>;

// ----------------------
// Export PDF (blob; not validated)
// POST /api/rfi/pending-changes/:id/export/
// ----------------------
export const exportPdf = {
  body: z.any().optional(),
  response: z.any(),
  params: z.object({
    pending_change_id: z.coerce.number(),
  }),
  query: z.object({
    project: z.coerce.number(),
  }),
};
const exportPdfSchema = z.object(exportPdf);
export type ExportPdf = z.infer<typeof exportPdfSchema>;
